/** @format */
import React from "react";
import { Icons } from "assets";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Tooltip,
  tooltipClasses,
  TooltipProps,
} from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import styled from "@emotion/styled";
import { mainColor } from "theme";
import { TextCus } from "components/TextCus";
import useMode from "hooks/useMode";

interface CommonSelectProps {
  options: { id: string; name: string }[];
  value?: string;
  onChange: (event: SelectChangeEvent<any>) => void;
  width?: string;
  label?: string;
  titleTooltip?: string;
  action?: () => void;
  isShowLabel?: boolean;
  sx?: any
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 10 + ITEM_PADDING_TOP,
      width: 200,
    },
  },
};

const CustomSelect = styled(Select)(() => ({
  backgroundColor: mainColor.primary,
  borderRadius: "5px",
  zIndex: "999",
  height: "40px",

  "& .MuiSelect-select.MuiSelect-standard.MuiInputBase-input.MuiInput-input": {
    paddingRight: "0px !important",
  },
  "& .MuiInputBase-root.MuiInput-root.MuiInput-underline": {
    "::before": {
      content: "none",
    },
    "::after": {
      content: "none",
    },
  },
  "& .MuiFormLabel-root.MuiInputLabel-root": {
    display: "none !important",
  },
  "& .MuiSelect-icon": {
    color: "#fff",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  padding: "10px 16px",
}));

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: mainColor.third,
    color: mainColor.secondary,
    boxShadow: "white",
    fontSize: 11,
    maxWidth: "150px",
  },
}));

const SelectCus = ({
  options,
  onChange,
  width,
  label,
  titleTooltip,
  action,
  isShowLabel,
  ...selectProps
}: CommonSelectProps) => {
  const mode = useMode();
  return (
    <LightTooltip title={titleTooltip} placement="top" >
      <FormControl fullWidth variant="outlined">
        {isShowLabel && (
          <TextCus
            color={mode.textMain}
            fontWeight={"600"}
            fontSize={"14px"}
            mb={1}
          >
            {label}
          </TextCus>
        )}
        <CustomSelect
          sx={{ width: width ?? "100%" }}
          IconComponent={() => (
            <Box sx={{ pointerEvents: "inherit" }} pl={"5px"}>
              {Icons.ArrowSelect('white')}
            </Box>
          )}
          variant="standard"
          displayEmpty
          onChange={onChange}
          MenuProps={MenuProps}
          renderValue={(selected: string) => {
            const select = options?.find((item) => item.id === selected);
            if (!selected) {
              return <TextCus
                color={"white"}
                useI18n={true}
                fontSize={14}
                fontWeight={"600"}
                variant={'bodyBold'}
              >
                {label}
              </TextCus>;
            }
            return <TextCus
              color={"white"}
              useI18n={true}
              fontSize={14}
              fontWeight={"600"}
              variant={'bodyBold'}
            >
              {select.name}
            </TextCus>;
          }}
          {...selectProps}
        >
          {label && (
            <MenuItem
              value=""
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {action ? (
                <Button
                  fullWidth
                  onClick={action}
                  size="small"
                  variant="outlined"
                >
                  <TextCus sx={{ fontSize: "12px" }}>Tạo mới</TextCus>
                </Button>
              ) : (
                <em style={{ fontSize: "13px", width: "100%" }}>{label}</em>
              )}
            </MenuItem>
          )}
          {options?.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </CustomSelect>
      </FormControl>
    </LightTooltip>
  );
};

export default SelectCus;
